import { zodResolver } from "@hookform/resolvers/zod";
import { match, useGetReservationByIdQuery } from "@stepapp/core";
import { CategoryType, ReplacementCheckoutInput } from "@stepapp/core/apiTypes";
import { endOfWeek, format, startOfWeek } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import ErrorModal from "../../components/Modal/ErrorModal";
import { serviceDetailsSchema } from "../../schemas";
import { setEndDate, setStartDate } from "../../store/calendar";
import { EditReservationQuestionType } from "../../store/types";
import EditReservationAddition from "./EditReservationAddition";
import EditReservationDate from "./EditReservationDate";
import EditReservationLoading from "./EditReservationLoading";
import EditReservationNote from "./EditReservationNote";
import EditReservationSuccess from "./EditReservationSuccess";

type StatusType = "success" | "loading" | "";

type EditReservationScreenParams = {
  reservationId: string;
  questionToEdit: string;
  handleGoBack: () => void;
  setNewReservationId: (v: string) => void;
};

const EditReservationScreen = ({
  reservationId,
  questionToEdit,
  handleGoBack,
  setNewReservationId,
}: EditReservationScreenParams) => {
  const appDispatch = useDispatch();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [detailsStatus, setDetailsStatus] = useState<StatusType>("");

  const [questionType, questionId] = questionToEdit.split(".") as [
    EditReservationQuestionType,
    string | undefined,
  ];

  const { data: reservationData, refetch: reservationRefetch } = useGetReservationByIdQuery({
    variables: { orderId: reservationId, isCustomerApp: true },
  });
  const reservation = reservationData?.getReservationById;

  const servicesToOrder = reservation?.services?.filter(
    (service) => !service?.variants?.some((variant) => variant?.isFree),
  );

  const defaultValues = useMemo(
    () => ({
      attributes:
        reservation?.attributes.flatMap((attribute) => ({
          attributeId: attribute?.attribute?.id || "",
          quantity: attribute?.quantity || 0,
          valueId: attribute?.values.map((v) => v?.id || "") || [],
        })) || [],
      paymentMethodId: reservation?.payment?.paymentMethodId || "",
      services:
        (servicesToOrder || [])?.flatMap((s) => ({
          serviceId: s?.service.id || "",
          variants:
            s?.variants.map((v) => ({
              quantity: v.quantity,
              variantId: v.variantId,
            })) || [],
        })) || [],
      startDate: reservation?.startDate || "",
    }),
    [
      reservation?.payment?.paymentMethodId,
      reservation?.attributes,
      servicesToOrder,
      reservation?.startDate,
    ],
  );

  const form = useForm<ReplacementCheckoutInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(serviceDetailsSchema),
    defaultValues,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    const startDate = startOfWeek(reservation?.startDate || new Date()).toISOString();
    const endDate = endOfWeek(reservation?.startDate || new Date()).toISOString();

    appDispatch(setStartDate(format(startDate, "yyyy-MM-dd")));
    appDispatch(setEndDate(format(endDate, "yyyy-MM-dd")));
  }, []);

  const handleCloseSuccessEdit = useCallback(() => {
    handleGoBack();
    setDetailsStatus("");
  }, [handleGoBack]);

  if (detailsStatus === "loading") {
    return <EditReservationLoading />;
  }

  if (detailsStatus === "success") {
    return (
      <EditReservationSuccess questionType={questionType} handleClose={handleCloseSuccessEdit} />
    );
  }

  return (
    <>
      {match({ questionType })
        .with({ questionType: "date" }, () => (
          <EditReservationDate
            setDetailsStatus={setDetailsStatus}
            setShowErrorModal={setErrorMsg}
            setNewReservationId={setNewReservationId}
            handleGoBack={handleGoBack}
            reservationId={reservationId}
            reservation={reservation}
          />
        ))
        .with({ questionType: "questions" }, { questionType: "services" }, () => (
          <EditReservationAddition
            questionType={questionType}
            questionId={questionId}
            reservationId={reservationId}
            reservation={reservation}
            form={form}
            setDetailsStatus={setDetailsStatus}
            setShowErrorModal={setErrorMsg}
            handleGoBack={handleGoBack}
            setNewReservationId={setNewReservationId}
          />
        ))
        .with({ questionType: "customerNote" }, () => (
          <EditReservationNote
            customerNote={reservation?.customerNote || ""}
            reservationId={reservationId}
            setDetailsStatus={setDetailsStatus}
            handleGoBack={handleGoBack}
            refetch={reservationRefetch}
            categoryType={reservation?.bundle?.categoryType || CategoryType.Cleaning}
          />
        ))
        .otherwise(() => null)}

      <ErrorModal
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
        fallbackErrorMsg="alerts.cantUpdateReservtion"
      />
    </>
  );
};

export default EditReservationScreen;
